import React from "react";
import SEO from "@/components/SEO";
import Hero from "@/components/Hero";
import BookingWidget from "@/components/BookingWidget";
import ReviewsSection from "@/components/ReviewsSection";
import Fleet from "@/components/Fleet";
import TrustSection from "@/components/TrustSection";
import CTASection from "@/components/CTASection";
import { useTranslation } from "react-i18next";
import { track } from "@/lib/track";

const Home = () => {
  const { t, i18n } = useTranslation();

  React.useEffect(() => { track("page_view", { path: "/" }); }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "TaxiService"],
    "@id": "https://vladriver-vtc.fr/#business",
    "name": "VlaDriver VTC — Chauffeur Privé Nice & Côte d'Azur",
    "description": "Chauffeur VTC Nice et chauffeur privé Côte d'Azur pour transferts aéroport Nice, Monaco, Cannes, Saint-Tropez, Antibes. Service 24/7, tarifs forfaitaires, chauffeur multilingue.",
    "image": "https://customer-assets.emergentagent.com/job_vtc-riviera-boost/artifacts/nkjngjkp_1000002234.png",
    "url": "https://vladriver-vtc.fr",
    "telephone": "+33783977370",
    "email": "nicusor.bocea52@gmail.com",
    "priceRange": "€€",
    "currenciesAccepted": "EUR",
    "paymentAccepted": "Cash, Credit Card, Bank Transfer",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Cagnes-sur-Mer",
      "addressRegion": "Alpes-Maritimes",
      "postalCode": "06800",
      "addressCountry": "FR"
    },
    "areaServed": [
      { "@type": "City", "name": "Nice" },
      { "@type": "City", "name": "Monaco" },
      { "@type": "City", "name": "Cannes" },
      { "@type": "City", "name": "Antibes" },
      { "@type": "City", "name": "Saint-Tropez" },
      { "@type": "City", "name": "Menton" },
      { "@type": "AdministrativeArea", "name": "Côte d'Azur" }
    ],
    "openingHoursSpecification": [{
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    }],
    "sameAs": [
      "https://g.page/r/CWM_ouLn_9JqEBM"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Transferts VTC Côte d'Azur",
      "itemListElement": [
        {
          "@type": "Offer",
          "price": "90",
          "priceCurrency": "EUR",
          "itemOffered": {
            "@type": "Service",
            "name": "VTC Nice → Monaco",
            "url": "https://vladriver-vtc.fr/vtc-nice-monaco",
            "serviceType": "Transfert VTC",
            "areaServed": { "@type": "City", "name": "Monaco" }
          }
        },
        {
          "@type": "Offer",
          "price": "80",
          "priceCurrency": "EUR",
          "itemOffered": {
            "@type": "Service",
            "name": "VTC Nice → Cannes",
            "url": "https://vladriver-vtc.fr/vtc-nice-cannes",
            "serviceType": "Transfert VTC",
            "areaServed": { "@type": "City", "name": "Cannes" }
          }
        },
        {
          "@type": "Offer",
          "price": "280",
          "priceCurrency": "EUR",
          "itemOffered": {
            "@type": "Service",
            "name": "VTC Nice → Saint-Tropez",
            "url": "https://vladriver-vtc.fr/vtc-nice-saint-tropez",
            "serviceType": "Transfert VTC longue distance",
            "areaServed": { "@type": "City", "name": "Saint-Tropez" }
          }
        },
        {
          "@type": "Offer",
          "price": "35",
          "priceCurrency": "EUR",
          "itemOffered": {
            "@type": "Service",
            "name": "Transfert Aéroport Nice",
            "url": "https://vladriver-vtc.fr/transfert-aeroport-nice",
            "serviceType": "Transfert aéroport",
            "areaServed": { "@type": "City", "name": "Nice" }
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Chauffeur Privé Monaco",
            "url": "https://vladriver-vtc.fr/chauffeur-prive-monaco",
            "serviceType": "Chauffeur privé",
            "areaServed": { "@type": "City", "name": "Monaco" }
          }
        }
      ]
    }
  };

  return (
    <>
      <SEO
        title="Chauffeur VTC Nice & Côte d'Azur — Transfert Aéroport, Monaco, Cannes | VlaDriver"
        description="Chauffeur VTC Nice et chauffeur privé Côte d'Azur. Transferts aéroport Nice, Monaco, Cannes, Saint-Tropez. Tarifs forfaitaires, réservation 24/7. Note 5/5."
        canonical="/"
        schema={schema}
        lang={i18n.language}
      />
      <Hero />

      {/* Uber-style booking widget — dynamic fare, pay online */}
      <section id="booking" data-testid="booking-section" className="py-16 md:py-24 bg-[#0A0A0A] border-y border-white/5 scroll-mt-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-12 max-w-2xl">
          <div className="text-center mb-8">
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-3 leading-tight">{t("home.widgetTitle")}</h2>
            <p className="text-white/60 text-base md:text-lg">{t("home.widgetSubtitle")}</p>
          </div>
          <BookingWidget />
        </div>
      </section>

      <ReviewsSection />
      <Fleet />
      <TrustSection />
      <CTASection />
    </>
  );
};

export default Home;
